import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Gem, LogOut, LayoutGrid, Palette, Files } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { BuilderProvider } from "@/lib/builderStore";
import { Toaster } from "sonner";
import { useIsMobile } from "@/lib/useIsMobile";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";

export default function AppShell() {
  const { user, logout, loading } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  useEffect(() => {
    if (!loading && !user) navigate("/login", { replace: true });
  }, [loading, user, navigate]);

  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-sm text-neutral-500">Loading…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Toaster position={isMobile ? "top-center" : "bottom-right"} richColors />

      {/* Top nav */}
      <header className="border-b hairline bg-white/80 backdrop-blur-sm sticky top-0 z-30">
        <div className="max-w-[1600px] mx-auto flex items-center justify-between px-4 lg:px-8 py-2 lg:py-4 gap-3">
          <div className="flex items-center gap-3 lg:gap-8 min-w-0">
            <div className="flex items-center gap-2">
              <Gem className="w-4 h-4" style={{ color: "var(--brand-accent)" }} />
              <span className="hidden lg:inline tracking-[0.32em] text-[11px] uppercase font-medium">Catalog Studio</span>
            </div>
            <nav className="flex items-center gap-1 text-sm">
              <NavItem to="/" icon={<LayoutGrid className="w-3.5 h-3.5" />} label="Builder" testid="nav-builder" end />
              <NavItem to="/presets" icon={<Palette className="w-3.5 h-3.5" />} label="Presets" testid="nav-presets" />
              <NavItem to="/library" icon={<Files className="w-3.5 h-3.5" />} label="Library" testid="nav-library" />
            </nav>
          </div>
          {/* Mobile: avatar menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button type="button" data-testid="mobile-user-menu" className="lg:hidden w-10 h-10 grid place-items-center rounded-full">
                <Avatar user={user} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-60">
              <DropdownMenuLabel>
                <div className="text-xs font-medium truncate">{user.name || user.email}</div>
                <div className="text-[10px] tracking-widest uppercase text-neutral-500 truncate">{user.email}</div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid="mobile-logout-btn" onSelect={logout} className="min-h-[48px] text-xs uppercase tracking-widest">
                <LogOut className="w-3.5 h-3.5 mr-2" /> Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <div className="hidden lg:flex items-center gap-4">
            <div className="text-right">
              <div className="text-xs font-medium" data-testid="current-user-name">{user.name || user.email}</div>
              <div className="text-[10px] tracking-widest uppercase text-neutral-500">{user.email}</div>
            </div>
            <Avatar user={user} testid="user-avatar" />
            <button
              data-testid="logout-btn"
              onClick={logout}
              className="text-xs uppercase tracking-widest text-neutral-500 hover:text-neutral-900 flex items-center gap-1"
            >
              <LogOut className="w-3.5 h-3.5" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="flex-1">
        <BuilderProvider>
          <Outlet />
        </BuilderProvider>
      </main>
    </div>
  );
}

function NavItem({ to, icon, label, testid, end }) {
  return (
    <NavLink
      to={to}
      end={end}
      data-testid={testid}
      className={({ isActive }) =>
        `flex items-center gap-2 px-3 py-2 min-h-[44px] min-w-[44px] justify-center rounded-sm transition-colors ${
          isActive ? "bg-neutral-900 text-white" : "text-neutral-600 hover:text-neutral-900 hover:bg-neutral-100"
        }`
      }
    >
      {icon}
      <span className="hidden sm:inline text-xs uppercase tracking-widest">{label}</span>
    </NavLink>
  );
}

function Avatar({ user, testid }) {
  return user.picture ? (
    <img src={user.picture} alt="" className="w-8 h-8 rounded-full object-cover" data-testid={testid} />
  ) : (
    <div className="w-8 h-8 rounded-full bg-neutral-800 text-white grid place-items-center text-xs" data-testid={testid}>
      {(user.name || user.email || "?").slice(0, 1).toUpperCase()}
    </div>
  );
}
